import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, Observable, combineLatest, of } from 'rxjs';
import { catchError, exhaustMap, filter, map, switchMap, tap } from 'rxjs/operators';

import {
  AppError,
  AppErrorCodes,
  ConfigApiService,
  InternalGateApiService,
  MapsApiService,
  Organisation,
  OrganizationApiService,
  Region,
  Subscription,
  Team,
  Token,
  User,
  UserApiService,
  UserHistoryAction,
  catchAppError,
  ErrorType,
} from '@core/api';
import { APP_CONFIG, AppConfig } from '@core/config';
import { GtagService, IntercomService, SocketService } from '@core/providers';

import { initActions } from '../actions/init.actions';
import { limitsActions } from '../actions/limits.actions';
import { paymentApiActions } from '../actions/payment.actions';
import { userActions, userApiActions } from '../actions/user.actions';
import { APP_STATES } from '../enums/app.enum';
import { AppComplete } from '../models/app.models';
import { MarketingUpgradeService } from '../services/marketing-upgrade.service';
import { TokenService } from '../services/token.service';

@Injectable()
export class InitEffects {
  private readonly config = inject<AppConfig>(APP_CONFIG);
  private actions$ = inject(Actions);
  private router = inject(Router);
  private token = inject(TokenService);
  private userApi = inject(UserApiService);
  private organisationApi = inject(OrganizationApiService);
  private internalGateApi = inject(InternalGateApiService);
  private configApi = inject(ConfigApiService);
  private mapsApi = inject(MapsApiService);
  private socket = inject(SocketService);
  private intercom = inject(IntercomService);
  private gtag = inject(GtagService);
  private marketingUpgrade = inject(MarketingUpgradeService);

  init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initActions.appInit),
      switchMap(() => this.checkInternalGate()),
      switchMap(() => this.verify().pipe(map((data) => initActions.appComplete(data)))),
      catchAppError((error) => initActions.appError({ error }), ErrorType.PAGE)
    )
  );

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.login),
      switchMap(({ email, password }) =>
        this.userApi.login(email, password).pipe(
          tap((token) => this.token.set(token)),
          switchMap(() => this.verify(true).pipe(map((data) => userApiActions.loginSuccess(data)))),
          catchAppError((error) => userApiActions.loginFail({ error, email }), ErrorType.CUSTOM)
        )
      )
    )
  );

  loginFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userApiActions.loginFail),
      filter(({ error }) => this.isSSOError(error)),
      map(({ error, email }) => {
        this.router.navigate(['/login/sso']);
        return userActions.setSSOError({ error, email });
      })
    )
  );

  initSSO$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.initSSO),
      switchMap(({ email }) =>
        this.userApi.initSSO(email).pipe(
          map(({ redirectUrl }) => userApiActions.initSSOSuccess({ redirectUrl })),
          catchAppError((error) => userApiActions.initSSOFail({ error }))
        )
      )
    )
  );

  initSSOSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userApiActions.initSSOSuccess),
        tap(({ redirectUrl }) => {
          window.location.href = redirectUrl;
        })
      ),
    { dispatch: false }
  );

  loginWithCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.loginWithCode),
      switchMap(({ code }) =>
        this.userApi.loginWithCode(code).pipe(
          tap((token) => this.token.set(token)),
          switchMap(() => this.verify(true).pipe(map((data) => userApiActions.loginSuccess(data)))),
          catchAppError((error) => userApiActions.loginFail({ error }), ErrorType.CUSTOM)
        )
      )
    )
  );

  postLoginNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userApiActions.loginSuccess),
        switchMap((data) => this.handlePostLoginNavigation(data))
      ),
    { dispatch: false }
  );

  processMarketingSubscriptionUpgradeEntry$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(initActions.processMarketingSubscriptionUpgradeEntry),
        switchMap(() => {
          const marketingContext = this.marketingUpgrade.get();
          return this.handleMarketingUpgradeNavigation({
            tier: marketingContext?.tier as Subscription | undefined,
          });
        })
      ),
    { dispatch: false }
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.logout),
      map(() => this.token.token),
      filter((token): token is Token => !!token?.access_token),
      switchMap((token) =>
        this.userApi.logout(token.refresh_token).pipe(
          map(() => userApiActions.logoutSuccess()),
          catchAppError(() => userApiActions.logoutSuccess(), ErrorType.CUSTOM)
        )
      )
    )
  );

  initPaymentCheck$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initActions.appComplete, userApiActions.loginSuccess),
      filter(({ status }) => status === APP_STATES.USER),
      switchMap(() => this.fetchPaymentMethodValidity().pipe(map((data) => paymentApiActions.initPayment(data))))
    )
  );

  initMapsLimitsCheck$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initActions.appComplete, userApiActions.loginSuccess),
      filter(({ status }) => status === APP_STATES.USER),
      map(() => limitsActions.fetchMapsBalance())
    )
  );

  logoutSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userApiActions.logoutSuccess),
        tap(() => {
          this.token.remove();
          this.socket.disconnect();
          this.intercom.shutdown();
          this.gtag.setUser(undefined);
        })
      ),
    { dispatch: false }
  );

  bootProviders$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(initActions.appComplete, userApiActions.loginSuccess),
        filter(({ user }) => !!user),
        tap(({ user, jwt }) => {
          if (user) {
            this.socket.connect(user);
            this.intercom.boot(user, jwt);
          }
        }),
        tap((data) => this.setGtagUserParams(data))
      ),
    { dispatch: false }
  );

  verifyInternalSession$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(initActions.appVerifyInternalSession),
        switchMap(() => this.checkInternalGate())
      ),
    { dispatch: false }
  );

  private verify(login = false): Observable<AppComplete> {
    if (this.token.token) {
      return combineLatest([
        this.fetchUserConfig(),
        this.userApi.fetch().pipe(
          switchMap((user) =>
            combineLatest([this.fetchSubscription(), this.fetchOrganisation(), this.fetchLayerConfig(), this.fetchJWT()]).pipe(
              exhaustMap(([subscription, { organisation, teams }, config, jwt]) =>
                this.createFirstMapIfNeeded(user, login, config.regions.regions).pipe(
                  map((mapId) => ({ user, subscription, organisation, teams, config, mapId, jwt }))
                )
              )
            )
          ),
          map((data) => ({ status: APP_STATES.USER, ...data })),
          catchAppError(() => of(null), ErrorType.PAGE)
        ),
      ]).pipe(
        map(([userConfig, data]) => {
          return data ? { ...data, config: { ...data.config, ...userConfig } } : { status: APP_STATES.CLIENT, config: { ...userConfig } };
        })
      );
    }

    return this.fetchUserConfig().pipe(map((config) => ({ status: APP_STATES.CLIENT, config })));
  }

  private fetchOrganisation(): Observable<{ organisation: Organisation; teams?: Team[] }> {
    return this.organisationApi.fetchOrganisation().pipe(
      switchMap((organisation) => {
        if (organisation) {
          return this.organisationApi.fetchTeams().pipe(map((teams) => ({ organisation, teams })));
        }
        return of({ organisation });
      })
    );
  }

  private fetchJWT() {
    return this.userApi.fetchJWT();
  }

  private fetchSubscription() {
    return this.userApi.fetchSubscription();
  }

  private fetchLayerConfig() {
    return combineLatest([
      this.configApi.getLayers(),
      this.configApi.getProjectTemplates(),
      this.configApi.getSubscriptionsPrices(),
      this.configApi.getPrintBorders(),
      this.configApi.fetchRegions(),
      this.configApi.getImportMethods(),
    ]).pipe(
      map(([{ basemaps, layers }, templates, subscriptionsPrices, printBorders, regions, importMethods]) => ({
        baseLayers: basemaps,
        dataLayers: layers,
        templates,
        subscriptionsPrices,
        printBorders,
        regions,
        importMethods,
      }))
    );
  }

  private fetchUserConfig() {
    return combineLatest([this.configApi.getSectors(true), this.configApi.getOrganisationTypes()]).pipe(
      map(([sectors, organisationTypes]) => ({
        sectors,
        organisationTypes,
      }))
    );
  }

  private fetchPaymentMethodValidity() {
    return this.userApi.getPaymentMethodValidity().pipe(
      map(() => ({ valid: true })),
      catchError(() => of({ valid: false }))
    );
  }

  private createFirstMapIfNeeded(user: User, login: boolean, regions: Region[]) {
    if (login && user.subscription === Subscription.FREE) {
      return this.mapsApi.listOwnedMaps({}).pipe(
        switchMap((maps) => {
          if (maps.length > 0) {
            return of(null);
          }
          const defaultRegion = regions?.find((region) => region?.default === true)?.id;
          if (!defaultRegion) {
            return of(null);
          }
          return this.mapsApi.create('New Map', [], { region: defaultRegion, allSubregions: true }).pipe(map((response) => response.id));
        })
      );
    }

    return of(null);
  }

  private setGtagUserParams(data?: AppComplete) {
    if (data && data.user && data.config) {
      const sector = data.config.sectors?.find((sector) => sector?.id === data.user?.sector)?.name || '';
      const orgType = data.config.organisationTypes?.find((orgType) => orgType?.id === data.user?.organisationType)?.name || '';
      const organisationName = data.organisation?.name;

      return this.gtag.setUser({
        user_id: data.user.id,
        org_type: orgType,
        subscription_tier: data.user.subscription.toLowerCase(),
        sector,
        ...(organisationName && { organisation_name: organisationName }),
      });
    }

    return this.gtag.setUser(undefined);
  }

  private checkInternalGate(): Observable<void> {
    const isInternalGateEnabled = this.config.internalGate ?? false;

    if (!isInternalGateEnabled) {
      return of(undefined);
    }

    return this.internalGateApi.verifySession().pipe(
      map(() => {
        const currentUrl = location.href;
        const isOnInternalLogin = currentUrl.includes('/internal-login');

        if (isOnInternalLogin) {
          this.router.navigate(['/login']);
        }

        return undefined;
      }),
      catchError(() => {
        this.router.navigate(['/internal-login']);
        return EMPTY;
      })
    );
  }

  private isSSOError(error: AppError) {
    return [
      AppErrorCodes.SSO_ENFORCED,
      AppErrorCodes.CLIENT_SIDE_SSO_CONFIGURATION_ERROR,
      AppErrorCodes.SERVER_SIDE_SSO_CONFIGURATION_ERROR,
      AppErrorCodes.SSO_USER_NOT_MATCHING,
    ].includes(error.code as AppErrorCodes);
  }

  private handlePostLoginNavigation(data: AppComplete): Observable<void> {
    if (data.status !== APP_STATES.USER) {
      this.navigateWithDefaultLoginFlow(data.mapId);
      return of(undefined);
    }

    const marketingContext = this.marketingUpgrade.get();
    if (marketingContext) {
      return this.handleMarketingUpgradeNavigation({
        fallbackToDefaultFlow: true,
        mapId: data.mapId,
        tier: marketingContext.tier as Subscription,
      });
    }

    const enteredFromMarketingLink = data.user?.notifications?.find(
      (notification) => notification.type === UserHistoryAction.UPGRADE_SUGGESTION
    );

    if (enteredFromMarketingLink && data.user) {
      this.navigateBasedOnMarketingNotification(data.user, true, data.mapId);
      return of(undefined);
    }

    this.navigateWithDefaultLoginFlow(data.mapId);
    return of(undefined);
  }

  private handleMarketingUpgradeNavigation(options?: {
    fallbackToDefaultFlow?: boolean;
    mapId?: string | null;
    tier?: Subscription;
  }): Observable<void> {
    const fallbackToDefaultFlow = options?.fallbackToDefaultFlow ?? false;
    return this.userApi.startMarketingSubscriptionUpgradeFlow(options?.tier).pipe(
      switchMap(() => this.userApi.fetch()),
      tap((user) => this.navigateBasedOnMarketingNotification(user, fallbackToDefaultFlow, options?.mapId)),
      map(() => undefined),
      tap(() => this.marketingUpgrade.clear()),
      catchAppError(() => {
        this.marketingUpgrade.clear();
        if (fallbackToDefaultFlow) {
          this.navigateWithDefaultLoginFlow(options?.mapId);
        } else {
          this.router.navigate(['/account/subscription/upgrade']);
        }
        return of(undefined);
      }, ErrorType.CUSTOM)
    );
  }

  private navigateBasedOnMarketingNotification(user: User, fallbackToDefaultFlow: boolean, mapId?: string | null): void {
    const enteredFromMarketingLink = user.notifications?.find((notification) => notification.type === UserHistoryAction.UPGRADE_SUGGESTION);

    if (!enteredFromMarketingLink) {
      if (fallbackToDefaultFlow) {
        this.navigateWithDefaultLoginFlow(mapId);
      } else {
        this.router.navigate(['/account/subscription/upgrade']);
      }
      return;
    }

    if (user.subscription !== Subscription.FREE) {
      this.router.navigate(['/account/subscription']);
    } else {
      const tier = enteredFromMarketingLink.data?.subscriptionTier;
      if (tier) {
        this.router.navigate(['/account/subscription/upgrade'], { state: { tier } });
      } else {
        this.router.navigate(['/account/subscription/upgrade']);
      }
    }
  }

  private navigateWithDefaultLoginFlow(mapId?: string | null) {
    const storedLocation = localStorage.getItem('last.location');

    if (storedLocation) {
      localStorage.removeItem('last.location');
      this.router.navigateByUrl(storedLocation);
      return;
    }

    if (mapId) {
      this.router.navigate(['/map', mapId]);
      return;
    }

    this.router.navigate(['/maps']);
  }
}
